import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { store } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import {
  fetchCaseById,
  setCaseInfo,
  setUnsavedCaseEditsContact,
} from '@/store/slices/caseSlice';
import { setCustomerInfo } from '@/store/slices/customerSlice';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import {
  getInitialOrCaptureContactId,
  hasActiveCallLeg,
} from '@/components/amazon-connect/contactUtils';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
} from '@gapartners/ui';
import {
  CaseFormDialog,
  type CaseFormFields,
} from '@/components/contact-center/CaseFormDialog';
import {
  isCaseClosed,
  DEFAULT_CASE_REPORTING_METHOD,
  attachContactIdToDescription,
} from '@/components/contact-center/caseFormConstants';
import { useCaseFormEditor } from '@/components/contact-center/useCaseFormEditor';
import { CloseCaseLoop } from '@/types/case';
import type { CustomerCase } from '@/types/customer';
import { mergeClosedCaseIntoFormFields } from '@/components/contact-center/mergeClosedCaseIntoFormFields';

export function AutoGeneratedCaseCard() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const directConnectLinkedWorkspaceId = useAppSelector(
    state => state.call.directConnectLinkedWorkspaceId
  );
  const customerWorkspaceId = useAppSelector(
    state => state.customer.workSpaceID
  );
  // Workspace resolved for this call: explicit link-customer action wins,
  // otherwise fall back to the workspace fetched with the call init data.
  // Same fallback pattern as CallHistoryCard.
  const linkedWorkspaceId =
    directConnectLinkedWorkspaceId ?? customerWorkspaceId;
  const callerName = useAppSelector(state => state.call.callerName) || '';
  const contactId = useAppSelector(state => state.call.contactId);
  const call = useAppSelector(state => state.call);
  const { activeContacts } = useAmazonConnectContacts();
  const activeContact = activeContacts.find(c => {
    if (!contactId || !hasActiveCallLeg(c)) return false;
    try {
      return (
        c.getContactId() === contactId ||
        getInitialOrCaptureContactId(c) === contactId
      );
    } catch {
      return false;
    }
  });
  const isOutboundCall = Boolean(activeContact && !activeContact.isInbound());
  const hasActiveCall = Boolean(contactId);
  const { currentCase, isLoading, error } = useAppSelector(state => state.case);
  const casePrefill = useAppSelector(state => state.call.casePrefill);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [relatedCase, setRelatedCase] = useState<CustomerCase | null>(null);
  const [formFields, setFormFields] = useState<CaseFormFields>({
    subject: '',
    description: '',
    status: '',
    category: '',
    categoryCode: '',
    categoryName: '',
    categoryPriority: '',
    caseType: '',
    caseTypeId: null,
    subCaseType: '',
    origination: '',
    closeCaseLoop: CloseCaseLoop.PHONE,
    resolution: '',
    phoneNumber: call.callerPhoneNumber || '',
    email: '',
    reportingMethod: DEFAULT_CASE_REPORTING_METHOD,
    notes: '',
    employeeId: null,
    reportedByEmployeeId: null,
    assignedTo: null,
    resolutionType: '',
    reportedByName: callerName || '',
    clientContactId: '',
  });

  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const isDirectConnect = useAppSelector(state => state.call.isDirectConnect);
  const currentUser = useAppSelector(state => state.auth.user);

  // Determine call type and auto-set closeCaseLoop + reportingMethod
  const callType = useMemo(() => {
    if (activeContact) {
      return connectAdapter.isChatContact(activeContact)
        ? CloseCaseLoop.CHAT
        : CloseCaseLoop.PHONE;
    }
    // After call ends (contact removed from activeContacts), fall back to the
    // closeCaseLoop already set on the form so post-call case creation still works.
    if (contactId) {
      return formFields.closeCaseLoop || CloseCaseLoop.PHONE;
    }
    return null;
  }, [activeContact, contactId, formFields.closeCaseLoop]);

  const defaultReportingMethod = useMemo(() => {
    if (!callType) return DEFAULT_CASE_REPORTING_METHOD;
    if (isDirectConnect) return 'ESP AHR Voice';
    return callType === CloseCaseLoop.CHAT ? 'ESP Chat' : 'ESP FOH Voice';
  }, [callType, isDirectConnect]);

  useEffect(() => {
    if (!createFormOpen || !callType) return;
    setFormFields(prev => ({
      ...prev,
      closeCaseLoop: callType,
      reportingMethod: prev.reportingMethod || defaultReportingMethod,
    }));
  }, [createFormOpen, callType, defaultReportingMethod]);

  const { useCreateCaseMutation } = api;
  const [createCase, { isLoading: isCreatingCase }] = useCreateCaseMutation();

  // Category-based case types for the create form
  const [fetchCaseTypesByCategory] = api.useLazyGetCaseTypesByCategoryQuery();
  const [filteredCaseTypeOptions, setFilteredCaseTypeOptions] = useState<
    Array<{ value: number; label: string }>
  >([]);

  const handleCategoryChangeForCreate = useCallback(
    async (categoryCode: string) => {
      if (!categoryCode) {
        setFilteredCaseTypeOptions([]);
        return;
      }
      try {
        const result = await fetchCaseTypesByCategory(categoryCode).unwrap();
        setFilteredCaseTypeOptions(
          (result.caseTypes ?? []).map(ct => ({
            value: ct.id,
            label: ct.name,
          }))
        );
      } catch {
        setFilteredCaseTypeOptions([]);
      }
    },
    [fetchCaseTypesByCategory]
  );

  // When category changes in the create form, fetch filtered case types
  const prevCategoryCodeRef = useRef(formFields.categoryCode);
  useEffect(() => {
    if (formFields.categoryCode !== prevCategoryCodeRef.current) {
      prevCategoryCodeRef.current = formFields.categoryCode;
      void handleCategoryChangeForCreate(formFields.categoryCode);
    }
  }, [formFields.categoryCode, handleCategoryChangeForCreate]);

  const handleContactChanged = useCallback(
    (contact: { phone?: string; email?: string }) => {
      setFormFields(prev => ({
        ...prev,
        phoneNumber: contact.phone ?? prev.phoneNumber,
        email: contact.email ?? prev.email,
      }));
    },
    []
  );

  const caseEditor = useCaseFormEditor({
    caseInfo: currentCase,
    contactId,
    syncWhen: dialogOpen && Boolean(currentCase),
    onAfterSave: caseId => {
      if (contactId) {
        dispatch(fetchCaseById({ caseId, contactId }));
      }
    },
    workspaceId: directConnectLinkedWorkspaceId
      ? Number(directConnectLinkedWorkspaceId)
      : customerWorkspaceId
        ? Number(customerWorkspaceId)
        : null,
  });

  // Default Assigned To to the current handling agent (match by email)
  useEffect(() => {
    if (!currentUser?.email) return;
    if (caseEditor.internalUserOptions.length === 0) return;
    const match = caseEditor.internalUserOptions.find(
      o => o.email?.toLowerCase() === currentUser.email.toLowerCase()
    );
    if (match) {
      setFormFields(prev => {
        if (prev.assignedTo != null) return prev;
        return { ...prev, assignedTo: match.value };
      });
    }
  }, [currentUser?.email, caseEditor.internalUserOptions]);

  const activeToast = toast ?? caseEditor.toast;

  // Publish unsaved drawer edits so the ACW Finish button can warn before it
  // clears this contact's case. A save in flight is not treated as unsaved.
  const hasUnsavedDrawerEdits =
    dialogOpen &&
    currentCase != null &&
    !isCaseClosed(currentCase.status) &&
    caseEditor.hasUnsavedChanges &&
    !caseEditor.isSaving;
  useEffect(() => {
    if (!hasUnsavedDrawerEdits || !contactId) return;
    dispatch(setUnsavedCaseEditsContact(contactId));
    return () => {
      dispatch(setUnsavedCaseEditsContact(null));
    };
  }, [dispatch, hasUnsavedDrawerEdits, contactId]);

  // Pre-fill create form from Boost AI metadata when no case exists
  const prefillAppliedRef = useRef(false);
  const pendingPrefillRef = useRef(false);

  // Reset form fields and prefill flags when a new call/contact starts
  const prevContactIdRef = useRef(contactId);
  useEffect(() => {
    if (contactId !== prevContactIdRef.current) {
      prevContactIdRef.current = contactId;
      prefillAppliedRef.current = false;
      pendingPrefillRef.current = false;
      setRelatedCase(null);
      setFormFields({
        subject: '',
        description: '',
        status: '',
        category: '',
        categoryCode: '',
        categoryName: '',
        categoryPriority: '',
        caseType: '',
        caseTypeId: null,
        subCaseType: '',
        origination: '',
        closeCaseLoop: CloseCaseLoop.PHONE,
        resolution: '',
        phoneNumber: call.callerPhoneNumber || '',
        email: '',
        reportingMethod: DEFAULT_CASE_REPORTING_METHOD,
        notes: '',
        employeeId: null,
        reportedByEmployeeId: null,
        assignedTo: null,
        resolutionType: '',
        reportedByName: callerName || '',
        clientContactId: '',
      });

      // Sync the global case + customer slices to the newly focused session's
      // data.  setCallStreamLoading already cleared the new session's copies to
      // null, so this prevents the previous contact's case / customer from
      // flashing in the cards.  When focus returns to a session that still has
      // data, it is restored.
      const state = store.getState();
      const focusedId = state.call.focusedContactId;
      const sessionCase =
        focusedId && state.call.sessions[focusedId]?.currentCase
          ? state.call.sessions[focusedId].currentCase
          : null;
      dispatch(setCaseInfo(sessionCase));
      const sessionCustomer =
        focusedId && state.call.sessions[focusedId]?.currentCustomer
          ? state.call.sessions[focusedId].currentCustomer
          : null;
      dispatch(setCustomerInfo(sessionCustomer));
    }
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (currentCase) return;
    if (!casePrefill) return;
    prefillAppliedRef.current = true;

    // Resolve display names from dropdown options if available.
    // Dropdowns use code values (e.g. "5-Low") but Boost AI may send labels ("Low").
    const resolveOption = (
      options: Array<{ value: string; label: string }>,
      raw: string | undefined
    ): string => {
      if (!raw) return '';
      const match = options.find(o => o.value === raw || o.label === raw);
      return match?.value ?? raw;
    };

    const matchedCategory = casePrefill.categoryCode
      ? caseEditor.categories.find(c => c.code === casePrefill.categoryCode)
      : undefined;
    const matchedCaseType =
      casePrefill.caseTypeId != null
        ? caseEditor.caseTypeOptions.find(
            o => o.value === casePrefill.caseTypeId
          )
        : undefined;
    const resolvedPriority = resolveOption(
      caseEditor.priorityOptions,
      casePrefill.priority
    );
    const resolvedOrigination = resolveOption(
      caseEditor.originationOptions,
      casePrefill.origination
    );

    setFormFields(prev => ({
      ...prev,
      subject: casePrefill.subject ?? prev.subject,
      description: casePrefill.description ?? prev.description,
      categoryCode: casePrefill.categoryCode ?? prev.categoryCode,
      category:
        matchedCategory?.name ?? casePrefill.categoryCode ?? prev.category,
      categoryName:
        matchedCategory?.name ?? casePrefill.categoryCode ?? prev.categoryName,
      caseTypeId: casePrefill.caseTypeId ?? prev.caseTypeId,
      caseType: matchedCaseType?.label ?? prev.caseType,
      subCaseType: casePrefill.subCaseType ?? prev.subCaseType,
      categoryPriority: resolvedPriority || prev.categoryPriority,
      resolution: casePrefill.resolution ?? prev.resolution,
      resolutionType: casePrefill.resolutionType ?? prev.resolutionType,
      origination: resolvedOrigination || prev.origination,
      phoneNumber: casePrefill.phoneNumber ?? prev.phoneNumber,
      email: casePrefill.email ?? prev.email,
    }));

    // If dropdown data wasn't loaded yet, flag for a second pass
    if (
      (casePrefill.categoryCode && !matchedCategory) ||
      (casePrefill.caseTypeId != null && !matchedCaseType)
    ) {
      pendingPrefillRef.current = true;
    }
  }, [
    casePrefill,
    currentCase,
    caseEditor.categories,
    caseEditor.caseTypeOptions,
    caseEditor.priorityOptions,
    caseEditor.originationOptions,
  ]);

  // Second pass: resolve display names after dropdown data loads
  useEffect(() => {
    if (!pendingPrefillRef.current) return;
    if (!casePrefill) return;

    const resolveOption = (
      options: Array<{ value: string; label: string }>,
      raw: string | undefined
    ): string => {
      if (!raw) return '';
      const match = options.find(o => o.value === raw || o.label === raw);
      return match?.value ?? raw;
    };

    const matchedCategory = casePrefill.categoryCode
      ? caseEditor.categories.find(c => c.code === casePrefill.categoryCode)
      : undefined;
    const matchedCaseType =
      casePrefill.caseTypeId != null
        ? caseEditor.caseTypeOptions.find(
            o => o.value === casePrefill.caseTypeId
          )
        : undefined;
    const resolvedPriority = resolveOption(
      caseEditor.priorityOptions,
      casePrefill.priority
    );
    const resolvedOrigination = resolveOption(
      caseEditor.originationOptions,
      casePrefill.origination
    );

    setFormFields(prev => {
      const updates: Partial<CaseFormFields> = {};
      if (matchedCategory && !prev.categoryName) {
        updates.category = matchedCategory.name;
        updates.categoryName = matchedCategory.name;
      }
      if (matchedCaseType && !prev.caseType) {
        updates.caseType = matchedCaseType.label;
      }
      if (resolvedPriority && !prev.categoryPriority) {
        updates.categoryPriority = resolvedPriority;
      }
      if (resolvedOrigination && !prev.origination) {
        updates.origination = resolvedOrigination;
      }
      if (Object.keys(updates).length === 0) return prev;
      pendingPrefillRef.current = false;
      return { ...prev, ...updates };
    });
  }, [
    casePrefill,
    caseEditor.categories,
    caseEditor.caseTypeOptions,
    caseEditor.priorityOptions,
    caseEditor.originationOptions,
  ]);

  // Set default "New" status from API options once they load
  const prevStatusOptionsLen = useRef(0);
  useEffect(() => {
    if (
      caseEditor.statusOptions.length > 0 &&
      caseEditor.statusOptions.length !== prevStatusOptionsLen.current &&
      !formFields.status
    ) {
      prevStatusOptionsLen.current = caseEditor.statusOptions.length;
      const newStatus = caseEditor.statusOptions.find(
        o => o.label.toLowerCase() === 'new'
      );
      if (newStatus) {
        setFormFields(prev => ({ ...prev, status: newStatus.value }));
      }
    }
  }, [caseEditor.statusOptions, formFields.status]);

  // When case is closed via edit dialog → close dialog and pre-fill create form
  useEffect(() => {
    if (!caseEditor.justClosed) return;

    setDialogOpen(false);
    // Pre-fill create form from the just-closed case so agent can create replacement
    setFormFields({
      ...caseEditor.caseFields,
      status: '', // reset status for new case
    });
    caseEditor.clearJustClosed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    caseEditor.justClosed,
    caseEditor.caseFields,
    caseEditor.clearJustClosed,
  ]);

  const handleCreateCase = async () => {
    if (!formFields.subject.trim()) {
      setToast({
        message: t('components.contactCenter.caseSubjectRequired'),
        type: 'error',
      });
      return;
    }

    if (!formFields.status.trim()) {
      setToast({
        message: t('components.contactCenter.caseStatusRequired'),
        type: 'error',
      });
      return;
    }

    if (!linkedWorkspaceId) {
      setToast({
        message: t('components.contactCenter.workspaceIdRequired'),
        type: 'error',
      });
      return;
    }

    if (!formFields.categoryCode || !formFields.categoryName.trim()) {
      setToast({
        message: t('components.contactCenter.caseCategoryRequired'),
        type: 'error',
      });
      return;
    }

    if (formFields.caseTypeId == null || formFields.caseTypeId <= 0) {
      setToast({
        message: t('components.contactCenter.caseTypeRequired'),
        type: 'error',
      });
      return;
    }

    if (!formFields.categoryPriority.trim()) {
      setToast({
        message: t('components.contactCenter.casePriorityRequired'),
        type: 'error',
      });
      return;
    }

    if (!callType) {
      setToast({
        message: t('components.contactCenter.autoGeneratedCasePlaceholder'),
        type: 'error',
      });
      return;
    }

    try {
      const response = await createCase({
        contactId: contactId || '',
        workspaceId: Number(linkedWorkspaceId) || 0,
        status: formFields.status,
        categoryCode: formFields.categoryCode,
        caseTypeId: formFields.caseTypeId,
        categoryPriority: formFields.categoryPriority || '',
        callerName: formFields.reportedByName || callerName || 'Unknown',
        reportedByName: formFields.reportedByName || undefined,
        subject: formFields.subject,
        description: attachContactIdToDescription(
          formFields.description,
          contactId,
          t('components.contactCenter.contactId')
        ),
        callType: callType === CloseCaseLoop.CHAT ? 'chat' : 'call',
        subCaseType: formFields.subCaseType,
        resolution: formFields.resolution,
        phoneNumber: formFields.phoneNumber || call.callerPhoneNumber || '',
        email: formFields.email,
        closeCaseLoop: callType,
        origination: formFields.origination,
        employeeId: formFields.employeeId ?? undefined,
        reportedByEmployeeId: formFields.reportedByEmployeeId ?? 0,
        assignedTo: formFields.assignedTo ?? undefined,
        clientContactId: formFields.clientContactId || undefined,
        resolutionType: formFields.resolutionType || undefined,
        reportingMethod: formFields.reportingMethod || undefined,
        internalNotes: formFields.notes || undefined,
        relatedCaseId:
          relatedCase?.id != null ? Number(relatedCase.id) : undefined,
      }).unwrap();

      if (contactId) {
        dispatch(fetchCaseById({ caseId: response.caseId, contactId }));
      }

      setCreateFormOpen(false);
      setRelatedCase(null);
      setToast(
        response.relatedCaseLinked === false
          ? {
              message: t(
                'components.contactCenter.caseCreatedRelatedCaseLinkFailed'
              ),
              type: 'warning',
            }
          : {
              message: t('components.contactCenter.caseCreatedSuccess'),
              type: 'success',
            }
      );

      // Reset form
      setFormFields({
        subject: '',
        description: '',
        status: '',
        category: '',
        categoryCode: '',
        categoryName: '',
        categoryPriority: '',
        caseType: '',
        caseTypeId: null,
        subCaseType: '',
        origination: '',
        closeCaseLoop: callType,
        resolution: '',
        phoneNumber: call.callerPhoneNumber || '',
        email: '',
        reportingMethod: DEFAULT_CASE_REPORTING_METHOD,
        notes: '',
        employeeId: null,
        reportedByEmployeeId: null,
        assignedTo: null,
        resolutionType: '',
        reportedByName: callerName || '',
        clientContactId: '',
      });
    } catch {
      setToast({
        message: t('components.contactCenter.caseCreationFailed'),
        type: 'error',
      });
    }
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const paperSx = {
    p: 2,
    ...(isOutboundCall && {
      opacity: 0.85,
      pointerEvents: 'none' as const,
    }),
  };

  const getCardTitle = (): string =>
    currentCase
      ? t('components.contactCenter.autoGeneratedCase')
      : t('components.contactCenter.createCase');

  const getCardSubtitle = (): string | null =>
    currentCase && linkedWorkspaceId
      ? t('components.contactCenter.createAnotherCase')
      : null;

  const renderCallGuardState = (): ReactNode | null => {
    if (!hasActiveCall) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('components.contactCenter.autoGeneratedCasePlaceholder')}
        </Typography>
      );
    }

    if (isOutboundCall) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('components.contactCenter.autoGeneratedCaseNotAvailableOutbound')}
        </Typography>
      );
    }

    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 96,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Typography variant="body2" color="error.main">
          {t('components.contactCenter.errorLoadingCase')}
        </Typography>
      );
    }

    return null;
  };

  const renderExistingCaseView = (): ReactNode => {
    if (!currentCase) return null;

    const isClosed = isCaseClosed(currentCase.status);

    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {t('common.id')}: {currentCase.id}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {t('components.contactCenter.subject')}: {currentCase.subject}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block', mb: 1.5 }}
        >
          {t('components.contactCenter.status')}: {currentCase.status}
        </Typography>
        {isClosed ? (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ display: 'block', mb: 1 }}
          >
            {t('components.contactCenter.reLinkToCreateReplacement')}
          </Typography>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={() => setDialogOpen(true)}
          >
            {t('components.contactCenter.editCase')}
          </Button>
        )}
      </>
    );
  };

  // Track workspace at close time to detect re-link
  const workspaceAtCloseRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      currentCase?.status &&
      isCaseClosed(currentCase.status) &&
      !workspaceAtCloseRef.current
    ) {
      workspaceAtCloseRef.current = directConnectLinkedWorkspaceId;
    }
    if (currentCase?.status && !isCaseClosed(currentCase.status)) {
      workspaceAtCloseRef.current = null;
    }
  }, [currentCase?.status, directConnectLinkedWorkspaceId]);

  const hasRelinkedWorkspace =
    currentCase?.status != null &&
    isCaseClosed(currentCase.status) &&
    workspaceAtCloseRef.current != null &&
    directConnectLinkedWorkspaceId != null &&
    directConnectLinkedWorkspaceId !== workspaceAtCloseRef.current;

  // Remember the last case so we can pre-fill the create form after a workspace
  // relink clears currentCase (CustomerInfoCardRefactored dispatches
  // clearCaseInfo on relink).
  const lastCaseRef = useRef<typeof currentCase>(null);
  useEffect(() => {
    if (currentCase) {
      lastCaseRef.current = currentCase;
      return;
    }
    // currentCase was cleared — pre-fill if we have a snapshot and a workspace.
    const prevCase = lastCaseRef.current;
    lastCaseRef.current = null;
    if (!prevCase || !directConnectLinkedWorkspaceId) return;

    setFormFields(prev =>
      mergeClosedCaseIntoFormFields(prevCase, prev, {
        categories: caseEditor.categories,
        caseTypeOptions: caseEditor.caseTypeOptions,
        priorityOptions: caseEditor.priorityOptions,
        originationOptions: caseEditor.originationOptions,
      })
    );
  }, [currentCase, directConnectLinkedWorkspaceId, caseEditor]);

  const renderLinkCustomerView = (): ReactNode => (
    <Typography variant="body2" color="text.secondary">
      {t('components.contactCenter.linkCustomerBeforeCase')}
    </Typography>
  );

  const renderCreateCaseView = (): ReactNode => (
    <>
      <CaseFormDialog
        mode="create"
        open={createFormOpen}
        onClose={() => setCreateFormOpen(false)}
        onSubmit={handleCreateCase}
        isLoading={isCreatingCase}
        formFields={formFields}
        onFormFieldChange={(field, value) => {
          setFormFields(prev => ({
            ...prev,
            [field]: value,
          }));
        }}
        statusOptions={caseEditor.statusOptions}
        subCaseTypeTree={caseEditor.subCaseTypeTree}
        originationOptions={caseEditor.originationOptions}
        priorityOptions={caseEditor.priorityOptions}
        caseTypeOptions={
          filteredCaseTypeOptions.length > 0
            ? filteredCaseTypeOptions
            : caseEditor.caseTypeOptions
        }
        categories={caseEditor.categories}
        contactId={contactId}
        closeCaseLoopSource="activeCall"
        activeCallCloseCaseLoop={callType}
        employeeOptions={caseEditor.employeeOptions}
        internalUserOptions={caseEditor.internalUserOptions}
        resolutionTypeOptions={caseEditor.resolutionTypeOptions}
        clientContactOptions={caseEditor.clientContactOptions}
        callerName={callerName}
        onContactChanged={handleContactChanged}
        relatedCaseWorkspaceId={linkedWorkspaceId ?? null}
        relatedCase={relatedCase}
        onRelatedCaseChange={setRelatedCase}
      />
      <Button
        variant="contained"
        fullWidth
        onClick={() => setCreateFormOpen(true)}
        sx={{ mt: 1 }}
      >
        {t('components.contactCenter.createCase')}
      </Button>
    </>
  );

  const renderCardBody = (): ReactNode => {
    const callGuardState = renderCallGuardState();
    if (callGuardState) return callGuardState;

    // Closed case: show as reference until workspace is re-linked
    if (currentCase && isCaseClosed(currentCase.status)) {
      if (!hasRelinkedWorkspace) return renderExistingCaseView();
      // Workspace re-linked — show create form (pre-filled from closed case)
      return renderCreateCaseView();
    }

    // Active case
    if (currentCase) return renderExistingCaseView();

    // No workspace linked yet
    if (!linkedWorkspaceId) return renderLinkCustomerView();

    return renderCreateCaseView();
  };

  const renderEditCaseDialog = (): ReactNode => {
    if (!currentCase) return null;
    if (isCaseClosed(currentCase.status)) return null;

    return (
      <CaseFormDialog
        mode="edit"
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={() => void caseEditor.handleSaveAll()}
        isLoading={caseEditor.isSaving}
        formFields={caseEditor.caseFields}
        onFormFieldChange={caseEditor.handleFormFieldChange}
        statusOptions={caseEditor.statusOptions}
        subCaseTypeTree={caseEditor.subCaseTypeTree}
        originationOptions={caseEditor.originationOptions}
        priorityOptions={caseEditor.priorityOptions}
        caseTypeOptions={caseEditor.caseTypeOptions}
        categories={caseEditor.categories}
        currentCase={currentCase}
        initialCaseFields={caseEditor.initialCaseFields}
        hasFieldChanged={caseEditor.hasFieldChanged}
        hasUnsavedChanges={caseEditor.hasUnsavedChanges}
        onDiscardNotes={caseEditor.handleDiscardNotes}
        contactId={contactId}
        closeCaseLoopSource="caseRecord"
        employeeOptions={caseEditor.employeeOptions}
        internalUserOptions={caseEditor.internalUserOptions}
        resolutionTypeOptions={caseEditor.resolutionTypeOptions}
        clientContactOptions={caseEditor.clientContactOptions}
        callerName={callerName}
        onContactChanged={handleContactChanged}
        relatedCaseWorkspaceId={linkedWorkspaceId ?? null}
      />
    );
  };

  return (
    <>
      <Paper variant="outlined" sx={paperSx}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {getCardTitle()}
        </Typography>
        {getCardSubtitle() && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: 'block' }}
          >
            {getCardSubtitle()}
          </Typography>
        )}
        {!getCardSubtitle() && <Box sx={{ mb: 1.5 }} />}
        {renderCardBody()}
      </Paper>

      {renderEditCaseDialog()}

      <Snackbar
        open={!!activeToast}
        onClose={() => {
          setToast(null);
          caseEditor.clearToast();
        }}
        message={activeToast?.message}
        variant="toast"
        severity={activeToast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </>
  );
}
